import { useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer/Footer";

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("leads");
  
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [newLead, setNewLead] = useState({ name: "", phone: "", email: "", address: "", interest: "Body Building", sourceTag: "Admin" });
  const [newOffer, setNewOffer] = useState({ title: "", description: "", imageUrl: "", storageId: "" });
  const [newSchedule, setNewSchedule] = useState({ className: "", day: "Monday", time: "", trainer: "" });
  const [newTeam, setNewTeam] = useState({ name: "", role: "" });
  const [newPricing, setNewPricing] = useState({ title: "", price: "", features: "" });
  const [editingPricingId, setEditingPricingId] = useState(null);
  const [editingLeadId, setEditingLeadId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const navigate = useNavigate();

  // Convex Queries
  const allLeads = useQuery("leads:get") || [];
  const leads = allLeads.filter(lead => 
    lead.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (lead.phone && lead.phone.includes(searchQuery))
  );
  const offers = useQuery("offers:get") || [];
  const schedule = useQuery("schedule:get") || [];
  const team = useQuery("team:get") || [];
  const gallery = useQuery("gallery:get") || [];

  // Convex Mutations
  const addLead = useMutation("leads:add");
  const updateLead = useMutation("leads:update");
  const addOffer = useMutation("offers:add");
  const removeOffer = useMutation("offers:remove");
  const addSchedule = useMutation("schedule:add");
  const removeSchedule = useMutation("schedule:remove");
  const addTeam = useMutation("team:add");
  const removeTeam = useMutation("team:remove");
  const addGallery = useMutation("gallery:add");
  const removeGallery = useMutation("gallery:remove");
  const generateOfferUploadUrl = useMutation("offers:generateUploadUrl");
  const generateGalleryUploadUrl = useMutation("gallery:generateUploadUrl");
  const posters = useQuery("posters:get") || [];
  const generatePosterUploadUrl = useMutation("posters:generateUploadUrl");
  const addPoster = useMutation("posters:add");
  const removePoster = useMutation("posters:remove");
  
  const pricing = useQuery("pricing:get") || [];
  const addPricing = useMutation("pricing:add");
  const removePricing = useMutation("pricing:remove");
  const updatePricing = useMutation("pricing:update");

  useEffect(() => {
    const isMock = localStorage.getItem("mockAdmin") === "true";
    if (!isMock) {
      navigate("/admin");
    }
  }, [navigate]);

  const handleDelete = async (collectionName, id) => {
    try {
      if (collectionName === "offers") await removeOffer({ id });
      if (collectionName === "schedule") await removeSchedule({ id });
      if (collectionName === "team") await removeTeam({ id });
      if (collectionName === "gallery") await removeGallery({ id });
      if (collectionName === "posters") await removePoster({ id });
      if (collectionName === "pricing") await removePricing({ id });
    } catch (err) {
      console.error("Error deleting doc:", err);
    }
  };

  const exportToCSV = () => {
    const headers = ["Client Name", "Contact", "Email", "Address", "Source", "Interest", "Status", "Plan", "Payment Method", "Form Completed", "Date"];
    const csvData = allLeads.map(lead => [
      lead.name,
      lead.phone,
      lead.email || "N/A",
      lead.address || "N/A",
      lead.sourceTag || "Website",
      lead.interest,
      lead.status || "Lead",
      lead.purchasedPlan || "-",
      lead.paymentMethod || "-",
      lead.googleFormCompleted ? "Yes" : "No",
      new Date(lead.timestamp || lead._creationTime).toLocaleDateString()
    ]);
    
    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    if (editingLeadId) {
      if (newLead.status === "Paid" && !newLead.googleFormCompleted) {
        alert("Please confirm the client has completed the Terms & Conditions Form.");
        return;
      }

      const finalStatus = newLead.status === "Paid" ? "Converted" : newLead.status;
      const discountText = newLead.discount ? ` (Discount: ${newLead.discount})` : "";
      const statusText = newLead.status ? ` [Status: ${newLead.status}]` : "";
      const finalComment = (newLead.notes || "") + discountText + statusText;

      await updateLead({
        id: editingLeadId,
        name: newLead.name,
        phone: newLead.phone,
        email: newLead.email,
        address: newLead.address,
        interest: newLead.interest,
        status: finalStatus,
        purchasedPlan: newLead.purchasedPlan,
        paymentMethod: newLead.status === "Paid" ? newLead.paymentMethod : undefined,
        googleFormCompleted: newLead.googleFormCompleted,
        comment: finalComment || undefined
      });
      setEditingLeadId(null);
      setActiveTab("leads");
    } else {
      await addLead(newLead);
      setActiveTab("posters");
    }
    setShowLeadModal(false);
    setNewLead({ name: "", phone: "", email: "", address: "", interest: "Body Building", sourceTag: "Admin", status: "Lead", purchasedPlan: "", paymentMethod: "Cash", googleFormCompleted: false, notes: "", discount: "" });
  };

  const handleCreateOffer = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let storageId = null;

      if (selectedFile) {
        // 1. Get the upload URL
        const postUrl = await generateOfferUploadUrl();
        // 2. POST the file
        const result = await fetch(postUrl, {
          method: "POST",
          headers: { "Content-Type": selectedFile.type },
          body: selectedFile,
        });
        const { storageId: sId } = await result.json();
        storageId = sId;
      }

      await addOffer({
        title: newOffer.title,
        description: newOffer.description,
        storageId: storageId || undefined,
      });

      setShowOfferModal(false);
      setNewOffer({ title: "", description: "", imageUrl: "", storageId: "" });
      setSelectedFile(null);
    } catch (err) {
      console.error("Error creating offer:", err);
      alert("Failed to publish offer.");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    await addSchedule(newSchedule);
    setShowScheduleModal(false);
    setNewSchedule({ className: "", day: "Monday", time: "", trainer: "" });
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    await addTeam(newTeam);
    setShowTeamModal(false);
    setNewTeam({ name: "", role: "" });
  };

  const handleCreatePricing = async (e) => {
    e.preventDefault();
    const pricingData = {
      title: newPricing.title,
      price: newPricing.price,
      features: newPricing.features.split(",").map(f => f.trim())
    };

    if (editingPricingId) {
      await updatePricing({ id: editingPricingId, ...pricingData });
      setEditingPricingId(null);
    } else {
      await addPricing(pricingData);
    }

    setShowPricingModal(false);
    setNewPricing({ title: "", price: "", features: "" });
  };

  const handleCreateGallery = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;
    setUploading(true);
    try {
      // 1. Get the upload URL
      const postUrl = await generateGalleryUploadUrl();
      // 2. POST the file
      const result = await fetch(postUrl, {
        method: "POST",
        headers: { "Content-Type": selectedFile.type },
        body: selectedFile,
      });
      const { storageId } = await result.json();

      // 3. Save reference
      await addGallery({ storageId });
      
      setShowGalleryModal(false);
      setSelectedFile(null);
    } catch (err) {
      console.error("Error creating gallery img:", err);
      alert("Failed to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // 1. Get upload URL
      const postUrl = await generatePosterUploadUrl();
      // 2. Upload file
      const result = await fetch(postUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      const { storageId } = await result.json();
      // 3. Save reference
      await addPoster({ storageId });
      alert("Poster uploaded successfully!");
    } catch (err) {
      console.error("Error uploading poster:", err);
      alert("Failed to upload poster.");
    } finally {
      setUploading(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem("mockAdmin");
    navigate("/admin");
  };

  return (
    <>
      <section className="admin-dashboard relative">
        <div className="login-banner relative justify-center flex">
          <h1 className="text-white absolute bottom-[25px] text-[3rem] font-bold">Admin Dashboard</h1>
        </div>
        
        <div className="container page-padding py-[5rem]">
          <div className="flex justify-between items-center mb-10 overflow-x-auto whitespace-nowrap">
            <div className="flex gap-8">
              {["leads", "posters", "pricing", "offers", "schedule", "team", "gallery"].map((tab) => (
                <button 
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`text-[1.8rem] font-bold py-2 px-6 capitalize ${activeTab === tab ? "text-[#ff0336] border-b-2 border-[#ff0336]" : "text-gray-500"}`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <button onClick={handleSignOut} className="bg-black text-white text-[1.4rem] font-bold py-3 px-8 hover:bg-[#1a1a1a]">
              Logout
            </button>
          </div>

          {activeTab === "leads" && (
            <div className="bg-white shadow-xl p-10 overflow-x-auto">
              <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
                <h2 className="text-[2.2rem] font-bold uppercase">Recent Enquiries</h2>
                <div className="flex gap-4">
                  <input 
                    type="text" 
                    placeholder="Search by name or phone..." 
                    className="px-4 py-3 border border-gray-300 rounded-md text-[1.4rem] outline-none"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button onClick={exportToCSV} className="bg-green-600 text-white px-8 py-3 text-[1.4rem] font-bold uppercase hover:bg-green-700 transition-all">
                    Export to Sheets
                  </button>
                  <button onClick={() => { setEditingLeadId(null); setNewLead({ name: "", phone: "", email: "", address: "", interest: "Body Building", sourceTag: "Admin", status: "Lead", purchasedPlan: "", paymentMethod: "Cash", googleFormCompleted: false, notes: "", discount: "" }); setShowLeadModal(true); }} className="bg-[#ff0336] text-white py-3 px-8 text-[1.4rem] font-bold hover:bg-[#ff0336ce]">
                    + Add Lead
                  </button>
                </div>
              </div>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="py-4 px-6 text-[1.6rem] font-bold">No.</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Date</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Name</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Phone</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Email</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Address</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Source</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Interest</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Status</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Plan</th>
                    <th className="py-4 px-6 text-[1.6rem] font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => {
                    const absIndex = allLeads.findIndex(l => l._id === lead._id);
                    const leadNo = allLeads.length - absIndex;
                    return (
                    <tr key={lead._id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6 text-[1.4rem] font-bold">#{leadNo.toString().padStart(3, '0')}</td>
                      <td className="py-4 px-6 text-[1.4rem] whitespace-nowrap">{new Date(lead.timestamp || lead._creationTime).toLocaleDateString()}</td>
                      <td className="py-4 px-6 text-[1.4rem]">{lead.name}</td>
                      <td className="py-4 px-6 text-[1.4rem]">{lead.phone}</td>
                      <td className="py-4 px-6 text-[1.4rem]">{lead.email || "N/A"}</td>
                      <td className="py-4 px-6 text-[1.4rem]">{lead.address || "N/A"}</td>
                      <td className="py-4 px-6 text-[1.4rem] font-bold text-[#ff0336]">{lead.sourceTag || "Website"}</td>
                      <td className="py-4 px-6 text-[1.4rem]">{lead.interest}</td>
                      <td className="py-4 px-6 text-[1.4rem]">
                        <span className={`px-4 py-1 rounded-full text-white font-bold ${lead.status === "Converted" ? "bg-green-500" : "bg-gray-400"}`}>
                          {lead.status || "Lead"}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-[1.4rem] font-bold">{lead.purchasedPlan || "-"}</td>
                      <td className="py-4 px-6 text-[1.4rem]">
                        <button 
                          onClick={() => {
                            setEditingLeadId(lead._id);
                            setNewLead({
                              name: lead.name || "",
                              phone: lead.phone || "",
                              email: lead.email || "",
                              address: lead.address || "",
                              interest: lead.interest || "Body Building",
                              sourceTag: lead.sourceTag || "Admin",
                              status: lead.status === "Converted" ? "Paid" : lead.status || "Lead",
                              purchasedPlan: lead.purchasedPlan || "",
                              paymentMethod: lead.paymentMethod || "Cash",
                              googleFormCompleted: lead.googleFormCompleted || false,
                              notes: "",
                              discount: ""
                            });
                            setActiveTab("editLead");
                          }}
                          className="bg-blue-600 text-white px-4 py-2 font-bold text-[1.2rem] rounded hover:bg-blue-700"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                  {leads.length === 0 && <tr><td colSpan="11" className="py-10 text-center text-[1.6rem]">No leads found</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "editLead" && (
            <div className="bg-white shadow-xl p-10 animate-fadeIn">
              <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
                <h2 className="text-[2.6rem] font-bold uppercase">Edit Lead Details & Conversion</h2>
                <button 
                  onClick={() => { setActiveTab("leads"); setEditingLeadId(null); }} 
                  className="bg-black text-white py-3 px-8 text-[1.4rem] font-bold uppercase hover:bg-[#1a1a1a]"
                >
                  Back to Leads
                </button>
              </div>

              <div className="grid grid-cols-2 gap-10 md1000:grid-cols-1">
                {/* Client Details Form */}
                <form onSubmit={handleCreateLead} className="bg-[#f8f8f8] p-10 rounded-xl shadow-lg flex flex-col gap-6">
                  <h3 className="text-[2.2rem] font-bold mb-4 uppercase border-b pb-4">Client Details</h3>
                  <input className="text-[1.6rem] px-6 py-4 border outline-none" placeholder="Name *" required value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} />
                  <input className="text-[1.6rem] px-6 py-4 border outline-none" placeholder="Phone *" required value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} />
                  <input className="text-[1.6rem] px-6 py-4 border outline-none" placeholder="Email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} />
                  <input className="text-[1.6rem] px-6 py-4 border outline-none" placeholder="Address" value={newLead.address} onChange={(e) => setNewLead({ ...newLead, address: e.target.value })} />
                  <select className="text-[1.6rem] px-6 py-4 border outline-none" value={newLead.interest} onChange={(e) => setNewLead({ ...newLead, interest: e.target.value })}>
                    <option>Body Building</option>
                    <option>Boxing</option>
                    <option>Crossfit</option>
                    <option>Fitness</option>
                    <option>Yoga</option>
                  </select>
                  <button type="submit" className="bg-[#ff0336] text-white py-4 text-[1.8rem] font-bold uppercase mt-auto">Update Client Details</button>
                </form>

                {/* Conversion Details Form */}
                <form onSubmit={handleCreateLead} className="bg-[#f8f8f8] p-12 rounded-xl shadow-lg flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <label className="text-[1.6rem] font-bold">Select Plan *</label>
                    <select 
                      className="text-[1.6rem] px-6 py-4 border outline-none bg-white"
                      value={newLead.purchasedPlan || ""}
                      onChange={(e) => setNewLead({ ...newLead, purchasedPlan: e.target.value })}
                    >
                      <option value="">Select a plan</option>
                      {pricing.map(plan => (
                        <option key={plan._id} value={plan.title}>{plan.title} - ₹{plan.price}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[1.6rem] font-bold">Apply Offer/Discount</label>
                    <input 
                      className="text-[1.6rem] px-6 py-4 border outline-none"
                      placeholder="e.g. SUMMER50"
                      value={newLead.discount || ""}
                      onChange={(e) => setNewLead({ ...newLead, discount: e.target.value })}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[1.6rem] font-bold">Payment / Lead Status *</label>
                    <div className="flex gap-4">
                      {["Paid", "Enquired", "Lead"].map(status => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setNewLead({ ...newLead, status })}
                          className={`flex-1 py-4 text-[1.6rem] font-bold rounded-lg border-2 transition-all ${newLead.status === status ? "bg-[#ff0336] text-white border-[#ff0336]" : "bg-white text-gray-500 border-gray-200"}`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>

                  {newLead.status === "Paid" && (
                    <div className="flex flex-col gap-2">
                      <label className="text-[1.6rem] font-bold">Payment Method *</label>
                      <div className="flex gap-4">
                        {["Cash", "Card", "UPI"].map(method => (
                          <button
                            key={method}
                            type="button"
                            onClick={() => setNewLead({ ...newLead, paymentMethod: method })}
                            className={`flex-1 py-4 text-[1.6rem] font-bold rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${newLead.paymentMethod === method ? "bg-black text-white border-black" : "bg-white text-gray-500 border-gray-200"}`}
                          >
                            <i className={`fa-solid ${method === "Cash" ? "fa-money-bill" : method === "Card" ? "fa-credit-card" : "fa-qrcode"}`}></i> {method}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {newLead.status === "Paid" && (
                    <div className="flex flex-col items-center gap-4 p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                      <label className="text-[1.6rem] font-bold text-center uppercase text-[#ff0336]">Terms & Conditions Agreement</label>
                      <p className="text-[1.4rem] text-gray-500 text-center mb-2">Please ensure the client has completed the agreement form before proceeding.</p>
                      <img 
                        src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://docs.google.com/forms/d/e/1FAIpQLScPjqpPEGxBBcZBtr_nZ3uazDVJzzVpYXAxBxbmVsHdXw_5dg/viewform?usp=publish-editor" 
                        alt="Google Form QR Code" 
                        className="w-[150px] h-[150px] border p-2 rounded shadow-sm"
                      />
                      <label className="flex items-center gap-3 mt-4 cursor-pointer bg-gray-50 p-4 rounded-lg w-full justify-center border border-gray-200 hover:bg-gray-100 transition-colors">
                        <input 
                          type="checkbox" 
                          className="w-8 h-8 accent-[#ff0336] cursor-pointer"
                          checked={newLead.googleFormCompleted}
                          onChange={(e) => setNewLead({ ...newLead, googleFormCompleted: e.target.checked })}
                        />
                        <span className="text-[1.6rem] font-bold">Client has completed Terms & Conditions Form</span>
                      </label>
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <label className="text-[1.6rem] font-bold">Additional Notes</label>
                    <textarea 
                      className="text-[1.6rem] px-6 py-4 border outline-none h-32"
                      placeholder="Special requests or payment notes..."
                      value={newLead.notes || ""}
                      onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                    />
                  </div>

                  <button 
                    type="submit"
                    disabled={newLead.status === "Paid" && !newLead.googleFormCompleted}
                    className="bg-[#ff0336] text-white py-5 text-[2rem] font-bold uppercase mt-auto hover:bg-black transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Confirm & Update Lead
                  </button>
                </form>
              </div>
            </div>
          )}

          {activeTab === "offers" && (
            <div className="bg-white shadow-xl p-10 overflow-x-auto">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-[2.2rem] font-bold uppercase">Offers / Announcements</h2>
                <button onClick={() => setShowOfferModal(true)} className="bg-[#ff0336] text-white py-3 px-8 text-[1.4rem] font-bold hover:bg-[#ff0336ce]">  + Add Offer </button>
              </div>
              <ul className="space-y-4">
                {offers.map(offer => (
                   <li key={offer._id} className="border p-6 rounded flex justify-between items-center bg-gray-50">
                      <div>
                         <h3 className="text-[1.8rem] font-bold">{offer.title}</h3>
                         <p className="text-[1.4rem] text-gray-500 mt-2">{offer.description}</p>
                      </div>
                      <button onClick={() => handleDelete("offers", offer._id)} className="bg-black text-white px-6 py-2 font-bold text-[1.4rem]">Delete</button>
                   </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === "schedule" && (
            <div className="bg-white shadow-xl p-10 overflow-x-auto">
              <div className="flex justify-between items-center mb-8">
                 <h2 className="text-[2.2rem] font-bold uppercase">Schedule Management</h2>
                 <button onClick={() => setShowScheduleModal(true)} className="bg-[#ff0336] text-white py-3 px-8 text-[1.4rem] font-bold hover:bg-[#ff0336ce]">  + Add Class </button>
              </div>
              <ul className="space-y-4">
                 {schedule.map(sc => (
                    <li key={sc._id} className="border p-6 rounded flex justify-between items-center bg-gray-50">
                       <span className="text-[1.6rem] font-bold">{sc.className} at {sc.time} on {sc.day} (Trainer: {sc.trainer})</span>
                       <button onClick={() => handleDelete("schedule", sc._id)} className="bg-black text-white px-6 py-2 font-bold text-[1.4rem]">Remove</button>
                    </li>
                 ))}
               </ul>
            </div>
          )}

          {activeTab === "team" && (
             <div className="bg-white shadow-xl p-10 overflow-x-auto">
                <div className="flex justify-between items-center mb-8">
                   <h2 className="text-[2.2rem] font-bold uppercase">Team Members</h2>
                   <button onClick={() => setShowTeamModal(true)} className="bg-[#ff0336] text-white py-3 px-8 text-[1.4rem] font-bold hover:bg-[#ff0336ce]">  + Add Trainer </button>
                </div>
                <ul className="space-y-4">
                 {team.map(member => (
                    <li key={member._id} className="border p-6 rounded flex justify-between items-center bg-gray-50">
                       <span className="text-[1.6rem] font-bold">{member.name} - {member.role}</span>
                       <button onClick={() => handleDelete("team", member._id)} className="bg-black text-white px-6 py-2 font-bold text-[1.4rem]">Remove</button>
                    </li>
                 ))}
               </ul>
             </div>
          )}

          {activeTab === "gallery" && (
             <div className="bg-white shadow-xl p-10 overflow-x-auto">
                <div className="flex justify-between items-center mb-8">
                   <h2 className="text-[2.2rem] font-bold uppercase">Gallery Management</h2>
                   <button onClick={() => setShowGalleryModal(true)} className="bg-[#ff0336] text-white py-3 px-8 text-[1.4rem] font-bold hover:bg-[#ff0336ce]">  + Add Image URL </button>
                </div>
                <div className="grid grid-cols-4 gap-6 md700:grid-cols-2">
                 {gallery.map(img => (
                    <div key={img._id} className="relative group">
                       <img src={img.url} alt="gallery" className="w-full h-40 object-cover rounded shadow" />
                       <button 
                         onClick={() => handleDelete("gallery", img._id)}
                         className="absolute top-2 right-2 bg-black bg-opacity-70 text-white p-2 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity"
                       >
                         Delete
                       </button>
                    </div>
                 ))}
               </div>
               {gallery.length === 0 && <p className="text-[1.6rem] text-center mt-10">No images in gallery</p>}
             </div>
          )}

          {activeTab === "posters" && (
            <div className="bg-white shadow-xl p-10">
              <h2 className="text-[2.2rem] font-bold mb-8 uppercase">Offer Posters & Graphics</h2>
              <div className="grid grid-cols-2 gap-10 md700:grid-cols-1 mb-10">
                <div className="border border-gray-200 p-8 rounded-lg flex flex-col gap-4">
                  <h3 className="text-[1.8rem] font-bold mb-4">Upload Poster</h3>
                  <input type="file" onChange={(e) => handleFileUpload(e, "offer_poster")} className="text-[1.4rem]" accept="image/*" />
                  {uploading && <p className="text-[1.2rem] text-[#ff0336]">Uploading...</p>}
                </div>
              </div>

              <div className="grid grid-cols-4 gap-6 md700:grid-cols-2">
                {posters.map((poster) => (
                  <div key={poster._id} className="relative group">
                    <img src={poster.url} alt="poster" className="w-full h-60 object-cover rounded shadow" />
                    <button 
                      onClick={() => handleDelete("posters", poster._id)}
                      className="absolute top-2 right-2 bg-black bg-opacity-70 text-white p-2 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
              {posters.length === 0 && <p className="text-[1.6rem] text-center mt-10">No posters uploaded yet</p>}
            </div>
          )}

          {activeTab === "pricing" && (
            <div className="bg-white shadow-xl p-10 overflow-x-auto">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-[2.2rem] font-bold uppercase">Pricing Plans</h2>
                <button 
                  onClick={() => {
                    setEditingPricingId(null);
                    setNewPricing({ title: "", price: "", features: "" });
                    setShowPricingModal(true);
                  }} 
                  className="bg-[#ff0336] text-white py-3 px-8 text-[1.4rem] font-bold hover:bg-[#ff0336ce]"
                >  
                  + Add Plan 
                </button>
              </div>
              <ul className="space-y-4">
                {pricing.map(plan => (
                   <li key={plan._id} className="border p-6 rounded flex justify-between items-center bg-gray-50">
                      <div>
                         <h3 className="text-[1.8rem] font-bold">{plan.title} - ₹{plan.price}</h3>
                         <p className="text-[1.4rem] text-gray-500 mt-2">{plan.features?.join(", ")}</p>
                      </div>
                      <div className="flex gap-4">
                        <button 
                          onClick={() => {
                            setEditingPricingId(plan._id);
                            setNewPricing({
                              title: plan.title,
                              price: plan.price,
                              features: plan.features?.join(", ") || ""
                            });
                            setShowPricingModal(true);
                          }} 
                          className="bg-[#ff0336] text-white px-6 py-2 font-bold text-[1.4rem]"
                        >
                          Edit
                        </button>
                        <button onClick={() => handleDelete("pricing", plan._id)} className="bg-black text-white px-6 py-2 font-bold text-[1.4rem]">Delete</button>
                      </div>
                   </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Modals */}
        {showLeadModal && !editingLeadId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex justify-center items-center p-4">
            <div className="bg-white w-[50rem] p-10 rounded-xl relative">
              <button onClick={() => setShowLeadModal(false)} className="absolute top-4 right-6 text-[3rem] text-[#ff0336]">&times;</button>
              <h3 className="text-[2.4rem] font-bold mb-8 uppercase">Create Manual Lead</h3>
              <form onSubmit={handleCreateLead} className="flex flex-col gap-6">
                <input className="text-[1.6rem] px-6 py-4 border outline-none" placeholder="Name *" required value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} />
                <input className="text-[1.6rem] px-6 py-4 border outline-none" placeholder="Phone *" required value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} />
                <input className="text-[1.6rem] px-6 py-4 border outline-none" placeholder="Email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} />
                <input className="text-[1.6rem] px-6 py-4 border outline-none" placeholder="Address" value={newLead.address} onChange={(e) => setNewLead({ ...newLead, address: e.target.value })} />
                <select className="text-[1.6rem] px-6 py-4 border outline-none" value={newLead.interest} onChange={(e) => setNewLead({ ...newLead, interest: e.target.value })}>
                  <option>Body Building</option>
                  <option>Boxing</option>
                  <option>Crossfit</option>
                  <option>Fitness</option>
                  <option>Yoga</option>
                </select>
                <button type="submit" className="bg-[#ff0336] text-white py-4 text-[1.8rem] font-bold uppercase">Save Lead</button>
              </form>
            </div>
          </div>
        )}

        {showOfferModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex justify-center items-center p-4">
            <div className="bg-white w-[50rem] p-10 rounded-xl relative">
              <button onClick={() => setShowOfferModal(false)} className="absolute top-4 right-6 text-[3rem] text-[#ff0336]">&times;</button>
              <h3 className="text-[2.4rem] font-bold mb-8 uppercase">Add Offer</h3>
               <form onSubmit={handleCreateOffer} className="flex flex-col gap-6">
                 <input className="text-[1.6rem] px-6 py-4 border" placeholder="Title (e.g. 50% OFF Summer)" required onChange={(e) => setNewOffer({ ...newOffer, title: e.target.value })} />
                 <textarea className="text-[1.6rem] px-6 py-4 border" placeholder="Description" required onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })} />
                 <div className="flex flex-col gap-2">
                   <label className="text-[1.4rem] font-bold">Offer Image (Optional)</label>
                   <input type="file" accept="image/*" onChange={(e) => setSelectedFile(e.target.files[0])} className="text-[1.4rem]" />
                 </div>
                 <button type="submit" disabled={uploading} className="bg-[#ff0336] text-white py-4 text-[1.8rem] font-bold uppercase disabled:bg-gray-400">
                   {uploading ? "Publishing..." : "Publish"}
                 </button>
               </form>
            </div>
          </div>
        )}

        {showScheduleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex justify-center items-center p-4">
            <div className="bg-white w-[50rem] p-10 rounded-xl relative">
              <button onClick={() => setShowScheduleModal(false)} className="absolute top-4 right-6 text-[3rem] text-[#ff0336]">&times;</button>
              <h3 className="text-[2.4rem] font-bold mb-8 uppercase">Add Schedule</h3>
              <form onSubmit={handleCreateSchedule} className="flex flex-col gap-6">
                <input className="text-[1.6rem] px-6 py-4 border" placeholder="Class Name" required onChange={(e) => setNewSchedule({ ...newSchedule, className: e.target.value })} />
                <select className="text-[1.6rem] px-6 py-4 border" required value={newSchedule.day} onChange={(e) => setNewSchedule({ ...newSchedule, day: e.target.value })}>
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                <input className="text-[1.6rem] px-6 py-4 border" placeholder="Time (e.g. 10:00 AM)" required onChange={(e) => setNewSchedule({ ...newSchedule, time: e.target.value })} />
                <input className="text-[1.6rem] px-6 py-4 border" placeholder="Trainer" required onChange={(e) => setNewSchedule({ ...newSchedule, trainer: e.target.value })} />
                <button type="submit" className="bg-[#ff0336] text-white py-4 text-[1.8rem] font-bold uppercase">Save</button>
              </form>
            </div>
          </div>
        )}

        {showTeamModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex justify-center items-center p-4">
            <div className="bg-white w-[50rem] p-10 rounded-xl relative">
              <button onClick={() => setShowTeamModal(false)} className="absolute top-4 right-6 text-[3rem] text-[#ff0336]">&times;</button>
              <h3 className="text-[2.4rem] font-bold mb-8 uppercase">Add Team Member</h3>
              <form onSubmit={handleCreateTeam} className="flex flex-col gap-6">
                <input className="text-[1.6rem] px-6 py-4 border" placeholder="Name" required onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} />
                <input className="text-[1.6rem] px-6 py-4 border" placeholder="Role (e.g. Fitness Coach)" required onChange={(e) => setNewTeam({ ...newTeam, role: e.target.value })} />
                <button type="submit" className="bg-[#ff0336] text-white py-4 text-[1.8rem] font-bold uppercase">Save</button>
              </form>
            </div>
          </div>
        )}

        {showGalleryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex justify-center items-center p-4">
            <div className="bg-white w-[50rem] p-10 rounded-xl relative">
              <button onClick={() => setShowGalleryModal(false)} className="absolute top-4 right-6 text-[3rem] text-[#ff0336]">&times;</button>
              <h3 className="text-[2.4rem] font-bold mb-8 uppercase">Add Gallery Image</h3>
              <form 
                onSubmit={handleCreateGallery} 
                className="flex flex-col gap-6"
                onDragEnter={handleDrag}
              >
                <div 
                  className={`flex flex-col gap-6 p-10 border-2 border-dashed rounded-xl text-center transition-colors ${dragActive ? "border-[#ff0336] bg-[#ff03350f]" : "border-gray-300"}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                   <label className="text-[1.8rem] font-bold cursor-pointer">
                      {selectedFile ? `Selected: ${selectedFile.name}` : "Drag & Drop or Click to Upload"}
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => setSelectedFile(e.target.files[0])} 
                      />
                   </label>
                   <p className="text-[1.4rem] text-gray-400">Supported formats: JPG, PNG, WEBP</p>
                </div>
                <button 
                  type="submit" 
                  disabled={uploading || !selectedFile} 
                  className="bg-[#ff0336] text-white py-4 text-[1.8rem] font-bold uppercase disabled:bg-gray-400"
                >
                  {uploading ? "Uploading..." : "Add Image"}
                </button>
              </form>
            </div>
          </div>
        )}

        {showPricingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex justify-center items-center p-4">
            <div className="bg-white w-[50rem] p-10 rounded-xl relative">
              <button 
                onClick={() => {
                  setShowPricingModal(false);
                  setEditingPricingId(null);
                  setNewPricing({ title: "", price: "", features: "" });
                }} 
                className="absolute top-4 right-6 text-[3rem] text-[#ff0336]"
              >
                &times;
              </button>
              <h3 className="text-[2.4rem] font-bold mb-8 uppercase">{editingPricingId ? "Edit Pricing Plan" : "Add Pricing Plan"}</h3>
              <form onSubmit={handleCreatePricing} className="flex flex-col gap-6">
                <input className="text-[1.6rem] px-6 py-4 border" placeholder="Plan Title (e.g. Pro)" required value={newPricing.title} onChange={(e) => setNewPricing({ ...newPricing, title: e.target.value })} />
                <input className="text-[1.6rem] px-6 py-4 border" placeholder="Price (e.g. 39)" required value={newPricing.price} onChange={(e) => setNewPricing({ ...newPricing, price: e.target.value })} />
                <textarea className="text-[1.6rem] px-6 py-4 border h-32" placeholder="Features (comma separated)" value={newPricing.features} onChange={(e) => setNewPricing({ ...newPricing, features: e.target.value })} />
                <button type="submit" className="bg-[#ff0336] text-white py-4 text-[1.8rem] font-bold uppercase">
                  {editingPricingId ? "Update Plan" : "Save Plan"}
                </button>
              </form>
            </div>
          </div>
        )}

        <Footer />
      </section>
    </>
  );
}

export default AdminDashboard;
